import Switch from "react-switch";
import { useState } from "react";
import { ADD_ONS, SUBSCRIPTIONS } from "../Pricing";
import Modal from "./portals/Modal";
import { useMemo } from "react";
import ConfirmPayment from "./popup/ConfirmPayment";
import NewPaymentMethod from "./popup/NewPaymentMethod";
import { Accordion, Button } from "./primitives";
import { useTranslation } from '../hooks/useTranslation';
import { catchErrorParse, ERRORS } from "../ErrorUtility";
import { useOutletContext } from "react-router-dom";
import { useDatabase } from "../contexts/DatabaseContext";

export default function WorkspaceAppsTab({ 
    workspace,
    newSubscriptionItem,
    setNewSubscriptionItem,
    setConfig,
    workspaceId,
    post,
    url,
    clientData,
    currentSubscriptionItem,
    hasChangesApps, requestedVisualTrigger
}) {
    const t = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [paymentError, setPaymentError] = useState(false);
    const [loading, setLoading] = useState(false);
    const { createSubscriptionActionEvent, updateWorkspaceConfigDoc } = useDatabase();

    async function saveAppsInc(payment_method = null, setError = null) {
        console.log('Save Apps:', newSubscriptionItem);
        setLoading(true);
        const [result, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/webapp/subscriptions/updateInc`, {
            newSubscriptionItem,
            payment_method,
        })
        if (errorCode === ERRORS.STRIPE_FAILED_PAYMENT_ERROR) {
            if (setError) {
                setError(errorMessage);
            } else {
                setPaymentError(errorMessage);
            }
        } else if (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    code: errorCode,
                    message: errorMessage ? errorMessage : error.message,
                    action: "inc_addons",
                    diabolical: true,
                }
            }));
        } else {
            updateWorkspaceConfigDoc("WorkspaceConfigs", { subscription: newSubscriptionItem }, true);
            setConfig(prev => ({ ...prev, subscription: newSubscriptionItem }));
            setIsOpen(false);
            const addonsBooleanArray = newSubscriptionItem.addons.map(addon => addon.enabled);
            createSubscriptionActionEvent("inc_addons", result.latest_invoice.total, workspace?.workspaceType, workspaceId, addonsBooleanArray);
        }
        setLoading(false);
    }

    async function saveAppsDec() {
        console.log('Save Apps:', newSubscriptionItem);
        setLoading(true);
        const [result, error, errorCode, errorMessage] = await catchErrorParse(post, `${url}/webapp/subscriptions/updateDec`, {
            newSubscriptionItem,
        })
        if (errorCode === ERRORS.STRIPE_FAILED_PAYMENT_ERROR) {
            setPaymentError(errorMessage);
        } else if (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    code: errorCode,
                    message: errorMessage ? errorMessage : error.message,
                    action: "dec_addons",
                    diabolical: true,
                }
            }));
        } else {
            setConfig(prev => ({ ...prev, subscription: newSubscriptionItem }));
            setIsOpen(false);
            const addonsBooleanArray = newSubscriptionItem.addons.map(addon => addon.enabled);
            createSubscriptionActionEvent("dec_addons", result.latest_invoice.total, workspace?.workspaceType, workspaceId, addonsBooleanArray);
        }
        setLoading(false);
    }

    function onToggle(index) {
        requestedVisualTrigger.current = index === 3 && !newSubscriptionItem.addons[index].enabled ? 'contact' : 'chat';
        setNewSubscriptionItem(prev => (
            {
                ...prev,
                addons: prev.addons.map((addon, i) => i === index ? { ...addon, enabled: !addon.enabled } : addon)
            }
        ));
    }

    const total = useMemo(() => {
        if (!newSubscriptionItem)
            return;
        let total = 0;
        for (let i = 0; i < newSubscriptionItem.addons.length; i++) {
            if (newSubscriptionItem.addons[i].enabled) {
                total += ADD_ONS[i].amount;
            }
        }
        total += SUBSCRIPTIONS[newSubscriptionItem.subscriptionType].amount;
        return total;
    }, [newSubscriptionItem]);

    const prevTotal = useMemo(() => {
        let prevTotal = 0;
        if (!currentSubscriptionItem)
            return;
        for (let i = 0; i < currentSubscriptionItem.addons.length; i++) {
            if (currentSubscriptionItem.addons[i].enabled) {
                prevTotal += ADD_ONS[i].amount;
            }
        }
        prevTotal += SUBSCRIPTIONS[currentSubscriptionItem.subscriptionType].amount;
        return prevTotal;
    }, [currentSubscriptionItem]);

    return (
        <div>
            <h2 className="mb-4">{t('Page Parrot Apps')}</h2>
            <div>{t('Add and remove applications below by turning them on and off.')}</div>
            <Accordion>
                {newSubscriptionItem && newSubscriptionItem.addons.map((addon, index) => <AddOn key={index} index={index} addon={ADD_ONS[index]} enabled={addon.enabled} onToggle={onToggle} />)}
            </Accordion>
            <div className="row-flex space-between mh-15">
                <h3>{t('Total')}:</h3>
                <h3>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total / 100)}</h3> {/* TODO: put this in pricing.js as a function call rather new Intl.NumberFormat each render*/}
            </div>
            
            <Button 
                className="submit settings"
                onClick={() => setIsOpen(true)}
                loading={loading}
                disabled={!hasChangesApps}
            >
                {t('Save')}
            </Button>
            <Modal open={isOpen} onClickOutside={() => setIsOpen(false)} lock={loading}>
                {paymentError ?
                    <NewPaymentMethod loading={loading} onSuccess={() => setIsOpen(false)} onClose={() => setIsOpen(false)} paymentError={paymentError} setPaymentError={setPaymentError} onSubmit={saveAppsInc} />
                    :
                    <ConfirmPayment 
                        loading={loading}
                        total={total}
                        prevTotal={prevTotal}
                        post={post}
                        url={url}
                        newSubscriptionItem={newSubscriptionItem}
                        currentSubscriptionItem={currentSubscriptionItem}
                        stripeCustomerId={clientData.stripeCustomerId}
                        onYesInc={() => saveAppsInc()}
                        onYesDec={() => saveAppsDec()}
                        onNo={() => setIsOpen(prev => !prev)}
                        onClose={() => setIsOpen(prev => !prev)} 
                    />
                }
            </Modal>
        </div>
    )
}


function AddOn({ addon, enabled, index, onToggle }) {

    const t = useTranslation();
    const { mobile } = useOutletContext();

    return (
        <Accordion.Item className={`addon-container ${mobile ? 'margin-lr-10' : ''}`}>
            <Accordion.Header noarrow={true} ignoreClasses={["addon-price-toggle"]} className="addon-header">
                <h4>{addon.name}</h4>
                <div className="addon-price-toggle">
                    <div>
                        {(addon.amount > 0) && (
                            <span className="addon-price">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(addon.amount / 100)}</span>
                        )}
                    </div>
                    <Switch onChange={() => onToggle(index)} checked={enabled} onColor="#27ca52" />
                </div>
            </Accordion.Header>
            <Accordion.Body>
                <div className="addon-body">
                    <div className="addon-description">
                        {addon.description}
                    </div>
                    <div className="addon-body-link">
                        <Button buttonType="submit">
                            {t('Learn More')}
                        </Button>
                    </div>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}